import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Doughnut, Bar, Line } from 'react-chartjs-2';

import Typography from '@material-ui/core/Typography';
import PeopleIcon from '@material-ui/icons/People';

import Paper from '~/components/Paper';
import GridContainer from '~/components/GridContainer';
import GridItem from '~/components/GridItem';
import LinearProgress from '~/components/LinearProgress';

import round from '~/utils/Number/round';
import formatNumber from '~/utils/Number/formatNumber';
import Request from '~/providers/Request';

import InputYearPicker from '~/components/Input/InputYearPicker';

function ShowCorporation() {
  const { t } = useTranslation();

  const [dataInit, setDataInit] = useState(moment().startOf('year').format());
  const [dataEnd, setDataEnd] = useState(moment().endOf('year').format());

  const [usersByMonth, setUsersByMonth] = useState(null);
  const [newUsersByMonth, setNewUsersByMonth] = useState(null);
  const [usersByPlatform, setUsersByPlatform] = useState([0, 0, 0]);
  const [loading, setLoading] = useState(false);

  const getBarUsersByMonth = () => {
    return {
      labels: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      datasets: [
        {
          label: t('dashboard.attributes.users'),
          borderWidth: 1,
          backgroundColor: '#40BDA7',
          borderColor: '#40BDA7',
          hoverBackgroundColor: '#40BDA7',
          hoverBorderColor: '#40BDA7',
          data: usersByMonth,
        },
      ],
    };
  };

  const getLineNewUsersByMonth = () => {
    return {
      labels: [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ],
      datasets: [
        {
          label: t('dashboard.attributes.users'),
          lineTension: 0.0,
          backgroundColor: 'rgba(78, 115, 223, 0.05)',
          borderColor: '#40BDA7',
          pointRadius: 3,
          pointBackgroundColor: '#40BDA7',
          pointBorderColor: '#40BDA7',
          pointHoverRadius: 3,
          pointHoverBackgroundColor: '#74CFBF',
          pointHoverBorderColor: '#74CFBF',
          pointHitRadius: 10,
          pointBorderWidth: 2,
          data: newUsersByMonth,
        },
      ],
    };
  };

  const getCountPlatform = (data, platform) => {
    const value = data.find((uuuuuuu) => uuuuuuu.platform === platform);
    if (value) {
      return round(value.count);
    }
    return 0;
  };

  const getPieUsersByPlatform = () => {
    return {
      labels: [
        `Android: ${usersByPlatform[0]}`,
        `IOS: ${usersByPlatform[1]}`,
        `${t('dashboard.attributes.unknown')}: ${usersByPlatform[2]}`,
      ],
      datasets: [
        {
          backgroundColor: ['#40BDA7', '#336699', '#FF6384'],
          hoverBackgroundColor: ['#40BDA7', '#336699', '#FF6384'],
          data: usersByPlatform,
        },
      ],
    };
  };

  const BarLineOptions = {
    scales: {
      xAxes: [
        {
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            fontSize: 16,
            maxTicksLimit: 12,
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            // maxTicksLimit: 12,
            padding: 10,
            fontSize: 16,
          },
          gridLines: {
            color: 'rgb(234, 236, 244)',
            zeroLineColor: 'rgb(234, 236, 244)',
            drawBorder: false,
            // borderDash: [2],
            // zeroLineBorderDash: [2],
          },
        },
      ],
    },
    legend: {
      labels: {
        fontSize: 16,
      },
    },
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      titleMarginBottom: 10,
      titleFontColor: '#6e707e',
      titleFontSize: 18,
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 20,
      yPadding: 20,
      displayColors: false,
      intersect: false,
      mode: 'index',
      caretPadding: 10,
    },
  };

  const pieOptions = {
    tooltips: {
      backgroundColor: 'rgb(255,255,255)',
      bodyFontColor: '#858796',
      borderColor: '#dddfeb',
      borderWidth: 1,
      xPadding: 15,
      yPadding: 15,
      displayColors: false,
      caretPadding: 10,
      callbacks: {
        label: function (tooltipItems, data) {
          let i;
          const label = [];
          const l = data.datasets.length;

          const total = data.datasets[0].data.reduce(
            (a, b) => Number(a) + Number(b),
            0
          );
          for (i = 0; i < l; i += 1) {
            const platform = data.labels[tooltipItems.index].split(':')[0];
            const value = data.datasets[0].data[tooltipItems.index];

            let percent = (value / total) * 100;
            percent = formatNumber(percent);

            label[i] = `${platform}: ${percent}%`;
          }
          return label;
        },
      },
    },
    legend: {
      display: true,
      labels: {
        fontSize: 16,
      },
    },
    cutoutPercentage: 60,
  };

  async function fetchUsers() {
    setLoading(true);
    Request.get(
      '/admin/dashboard/users',
      { start: dataInit, end: dataEnd },
      { normalizeJSON: false }
    )
      .then((response) => {
        setLoading(false);

        const newUsersMonths = [];
        const usersMonths = [];
        for (let i = 1; i <= 12; i++) {
          const usersMonth = response.usersByMonth.find(
            (data) => data.month === i
          );
          const newUsersMonth = response.newUsersByMonth.find(
            (data) => data.month === i
          );

          if (usersMonth) {
            usersMonths.push(usersMonth.count);
          } else {
            usersMonths.push(0);
          }

          if (newUsersMonth) {
            newUsersMonths.push(newUsersMonth.count);
          } else {
            newUsersMonths.push(0);
          }
        }
        setUsersByMonth(usersMonths);
        setNewUsersByMonth(newUsersMonths);

        const android = getCountPlatform(response.usersByPlatform, 'android');
        const ios = getCountPlatform(response.usersByPlatform, 'ios');
        const unknown = getCountPlatform(response.usersByPlatform, 'unknown');

        setUsersByPlatform([android, ios, unknown]);
      })
      .catch((e) => {
        setLoading(false);
        console.log(e);
      });
  }

  useEffect(() => {
    fetchUsers();
  }, [dataInit, dataEnd]);

  return (
    <>
      <Paper
        Icon={PeopleIcon}
        iconColor="blue"
        title={t('dashboard.tabs.users')}
        loading={false}
      >
        <GridContainer justify="center" alignItems="center">
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <InputYearPicker
              setStartDate={(date) => setDataInit(date)}
              setEndDate={(date) => setDataEnd(date)}
            />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <GridContainer justify="center" alignItems="center">
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <Typography variant="h6">
              {t('dashboard.attributes.count_users_by_month')}
            </Typography>
            <Bar data={getBarUsersByMonth} options={BarLineOptions} />
          </GridItem>

          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <Typography variant="h6">
              {t('dashboard.attributes.count_new_users_by_month')}
            </Typography>
            <Line data={getLineNewUsersByMonth} options={BarLineOptions} />
          </GridItem>
        </GridContainer>

        <LinearProgress loading={loading} />

        <GridContainer justify="center" alignItems="center">
          <GridItem xs={12} sm={6} md={6} lg={6} xl={6}>
            <Typography variant="h6">
              {t('dashboard.attributes.count_users_by_platform')}
            </Typography>
            <Doughnut data={getPieUsersByPlatform} options={pieOptions} />
          </GridItem>
        </GridContainer>
        <LinearProgress loading={loading} />
      </Paper>
    </>
  );
}

export default ShowCorporation;
